import { lazy, Suspense } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { OrganizationProvider } from "@/lib/organization-context";
import { OnboardingProvider } from "@/hooks/useOnboardingState";
import { SessionTracker } from "@/components/auth/SessionTracker";
import { AppLayout } from "@/components/layout/AppLayout";

import { DesktopOnlyGuard } from "@/components/layout/DesktopOnlyGuard";
import { SubscriptionGuard } from "@/components/auth/SubscriptionGuard";
import { SorvaineAdminGuard } from "@/components/auth/SorvaineAdminGuard";
import { AdminSupportGuard } from "@/components/auth/AdminSupportGuard";
import { AdminLayout } from "@/components/layout/AdminLayout";

// Landing & public marketing/SEO pages stay eager for instant first paint.
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Cookies from "./pages/Cookies";
import Security from "./pages/Security";
import Help from "./pages/Help";
import BookDemo from "./pages/BookDemo";
import Platform from "./pages/Platform";
import RfpResponseSoftware from "./pages/RfpResponseSoftware";
import BidManagementSoftware from "./pages/BidManagementSoftware";
import TenderManagementSoftware from "./pages/TenderManagementSoftware";
import LoopioAlternatives from "./pages/LoopioAlternatives";
import RfpioAlternatives from "./pages/RfpioAlternatives";
import BestRfpSoftware from "./pages/BestRfpSoftware";
import RfpResponseGenerator from "./pages/RfpResponseGenerator";
import Uk from "./pages/Uk";
import Uae from "./pages/Uae";
import Resources from "./pages/Resources";
import ResourceArticle from "./pages/ResourceArticle";
import WhatIsRfpResponseSoftware from "./pages/WhatIsRfpResponseSoftware";
import HowToRespondToAnRfp from "./pages/HowToRespondToAnRfp";
import BestRfpSoftwareArticle from "./pages/BestRfpSoftwareArticle";
import LoopioAlternativesArticle from "./pages/resources/LoopioAlternativesArticle";
import RfpioAlternativesArticle from "./pages/resources/RfpioAlternativesArticle";
import HowLongDoesRfpTake from "./pages/resources/HowLongDoesRfpTake";
import CommonRfpMistakes from "./pages/resources/CommonRfpMistakes";
import RfpResponseTemplate from "./pages/resources/RfpResponseTemplate";
import AiRfpSoftware from "./pages/resources/AiRfpSoftware";
import ProposalContentLibrary from "./pages/resources/ProposalContentLibrary";
import WhyIBuiltSorvaine from "./pages/resources/WhyIBuiltSorvaine";
import Unsubscribe from "./pages/Unsubscribe";
import { ScrollToTop } from "./components/ScrollToTop";
import { MarketingTracker } from "./components/system/MarketingTracker";

// Authenticated app routes are lazy-loaded so the editor (tiptap/prosemirror),
// charts (recharts) and other heavy code don't ship in the initial landing bundle.
const Auth = lazy(() => import("./pages/Auth"));
const Subscribe = lazy(() => import("./pages/Subscribe"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Projects = lazy(() => import("./pages/Projects"));
const NewProject = lazy(() => import("./pages/NewProject"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const Library = lazy(() => import("./pages/Library"));
const LibraryItemDetail = lazy(() => import("./pages/LibraryItemDetail"));
const Analytics = lazy(() => import("./pages/Analytics"));

// Admin pages are eager-imported so navigation inside /admin never flashes a Suspense fallback.
import AdminCtaAnalytics from "./pages/AdminCtaAnalytics";
import AdminProductRevenue from "./pages/AdminProductRevenue";
import AdminDashboard from "./pages/AdminDashboard";
import AdminBlogEditor from "./pages/AdminBlogEditor";
import AdminBlogAnalytics from "./pages/AdminBlogAnalytics";
// Eager-import per-tab admin components so switching tabs inside /admin doesn't trigger a Suspense flash
import { AdminOrganizationsTab } from "./components/admin/AdminOrganizationsTab";
import { AdminUsersTab } from "./components/admin/AdminUsersTab";
import { AdminSubscriptionsTab } from "./components/admin/AdminSubscriptionsTab";
import { AdminImpersonateTab } from "./components/admin/AdminImpersonateTab";
import { AdminUsageTab } from "./components/admin/AdminUsageTab";
import { AdminChurnTab } from "./components/admin/AdminChurnTab";
import { AdminFeatureFlagsTab } from "./components/admin/AdminFeatureFlagsTab";
import { AdminAnnouncementsTab } from "./components/admin/AdminAnnouncementsTab";
import { AdminTeamTab } from "./components/admin/AdminTeamTab";
import { AdminSettingsTab } from "./components/admin/AdminSettingsTab";
import { AdminAuditLogTab } from "./components/admin/AdminAuditLogTab";
import { AdminBlogTab } from "./components/admin/AdminBlogTab";
import { AdminNewsletterTab } from "./components/admin/AdminNewsletterTab";
import { AdminEmbedQueueTab } from "./components/admin/AdminEmbedQueueTab";
const DesignSystem = lazy(() => import("./pages/DesignSystem"));
const Settings = lazy(() => import("./pages/Settings"));
const ProposalWorkspacePage = lazy(() => import("./pages/ProposalWorkspacePage"));
const ComplianceWorkspacePage = lazy(() => import("./pages/ComplianceWorkspacePage"));
const SubmissionWorkspacePage = lazy(() => import("./pages/SubmissionWorkspacePage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Show cached data instantly on re-navigation; refresh quietly in the background.
      staleTime: 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function AppRoutes() {
  return (
    <Suspense fallback={null}>
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Index />} />
      <Route path="/auth" element={<DesktopOnlyGuard><Auth /></DesktopOnlyGuard>} />
      <Route path="/login" element={<DesktopOnlyGuard><Auth /></DesktopOnlyGuard>} />
      <Route path="/signup" element={<DesktopOnlyGuard><Auth /></DesktopOnlyGuard>} />
      <Route path="/subscribe" element={<DesktopOnlyGuard><Subscribe /></DesktopOnlyGuard>} />
      <Route path="/accept-invite" element={<DesktopOnlyGuard><AcceptInvite /></DesktopOnlyGuard>} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/book-demo" element={<BookDemo />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/platform" element={<Platform />} />
      <Route path="/security" element={<Security />} />
      <Route path="/help" element={<Help />} />
      <Route path="/rfp-response-software" element={<RfpResponseSoftware />} />
      <Route path="/bid-management-software" element={<BidManagementSoftware />} />
      <Route path="/tender-management-software" element={<TenderManagementSoftware />} />
      <Route path="/loopio-alternatives" element={<LoopioAlternatives />} />
      <Route path="/rfpio-alternatives" element={<RfpioAlternatives />} />
      <Route path="/best-rfp-software" element={<BestRfpSoftware />} />
      <Route path="/rfp-response-generator" element={<RfpResponseGenerator />} />
      <Route path="/uk" element={<Uk />} />
      <Route path="/uae" element={<Uae />} />
      

      {/* Blog (formerly Resources) — dedicated articles before catch-all. /resources/* kept for backward compat.
          ⚠️ DO NOT add <Navigate> redirects for individual blog slugs here. They will silently shadow
          real CMS posts published at the same slug (e.g. an "introducing-sorvaine" CMS post got hijacked
          by a redirect to "why-i-built-sorvaine"). If you need to retire a slug, either delete/unpublish
          the CMS row, or rename its slug — never redirect at the router. */}
      <Route path="/blog" element={<Resources />} />
      <Route path="/resources" element={<Resources />} />
      <Route path="/blog/what-is-rfp-response-software" element={<WhatIsRfpResponseSoftware />} />
      <Route path="/resources/what-is-rfp-response-software" element={<WhatIsRfpResponseSoftware />} />
      <Route path="/blog/how-to-respond-to-an-rfp" element={<HowToRespondToAnRfp />} />
      <Route path="/resources/how-to-respond-to-an-rfp" element={<HowToRespondToAnRfp />} />
      <Route path="/blog/best-rfp-software" element={<BestRfpSoftwareArticle />} />
      <Route path="/resources/best-rfp-software" element={<BestRfpSoftwareArticle />} />
      <Route path="/blog/loopio-alternatives" element={<LoopioAlternativesArticle />} />
      <Route path="/resources/loopio-alternatives" element={<LoopioAlternativesArticle />} />
      <Route path="/blog/rfpio-alternatives" element={<RfpioAlternativesArticle />} />
      <Route path="/resources/rfpio-alternatives" element={<RfpioAlternativesArticle />} />
      <Route path="/blog/how-long-does-an-rfp-take" element={<HowLongDoesRfpTake />} />
      <Route path="/resources/how-long-does-an-rfp-take" element={<HowLongDoesRfpTake />} />
      <Route path="/blog/common-rfp-mistakes" element={<CommonRfpMistakes />} />
      <Route path="/resources/common-rfp-mistakes" element={<CommonRfpMistakes />} />
      <Route path="/blog/rfp-response-template" element={<RfpResponseTemplate />} />
      <Route path="/resources/rfp-response-template" element={<RfpResponseTemplate />} />
      <Route path="/blog/ai-rfp-software" element={<AiRfpSoftware />} />
      <Route path="/resources/ai-rfp-software" element={<AiRfpSoftware />} />
      <Route path="/blog/proposal-content-library" element={<ProposalContentLibrary />} />
      <Route path="/resources/proposal-content-library" element={<ProposalContentLibrary />} />
      <Route path="/blog/why-i-built-sorvaine" element={<WhyIBuiltSorvaine />} />
      <Route path="/resources/why-i-built-sorvaine" element={<WhyIBuiltSorvaine />} />
      <Route path="/blog/:slug" element={<ResourceArticle />} />
      <Route path="/resources/:slug" element={<ResourceArticle />} />

      {/* Protected routes - require subscription */}
      <Route element={<AppLayout />}>
        <Route path="/dashboard" element={<SubscriptionGuard><Dashboard /></SubscriptionGuard>} />
        <Route path="/projects" element={<SubscriptionGuard><Projects /></SubscriptionGuard>} />
        <Route path="/projects/new" element={<SubscriptionGuard><NewProject /></SubscriptionGuard>} />
        <Route path="/projects/:id" element={<SubscriptionGuard><ProjectDetail /></SubscriptionGuard>} />
        <Route path="/projects/:id/proposal" element={<SubscriptionGuard><ProposalWorkspacePage /></SubscriptionGuard>} />
        <Route path="/projects/:id/compliance" element={<SubscriptionGuard><ComplianceWorkspacePage /></SubscriptionGuard>} />
        <Route path="/projects/:id/submission" element={<SubscriptionGuard><SubmissionWorkspacePage /></SubscriptionGuard>} />

        <Route path="/library" element={<SubscriptionGuard><Library /></SubscriptionGuard>} />
        <Route path="/library/:id" element={<SubscriptionGuard><LibraryItemDetail /></SubscriptionGuard>} />
        <Route path="/analytics" element={<SubscriptionGuard><Analytics /></SubscriptionGuard>} />

        <Route path="/design-system" element={<SubscriptionGuard><DesignSystem /></SubscriptionGuard>} />
        <Route path="/settings" element={<SubscriptionGuard><Settings /></SubscriptionGuard>} />
      </Route>

      {/* Admin Panel — sorvaine.com only, separate shell */}
      <Route path="/admin" element={<SorvaineAdminGuard><AdminLayout /></SorvaineAdminGuard>}>
        <Route index element={<AdminDashboard />} />
        <Route path="organisations" element={<AdminOrganizationsTab />} />
        <Route path="users" element={<AdminUsersTab />} />
        <Route path="subscriptions" element={<AdminSubscriptionsTab />} />
        <Route path="impersonate" element={<AdminSupportGuard><AdminImpersonateTab /></AdminSupportGuard>} />
        <Route path="usage" element={<AdminUsageTab />} />
        <Route path="churn" element={<AdminChurnTab />} />
        <Route path="cta-analytics" element={<AdminCtaAnalytics />} />
        <Route path="blog/new" element={<AdminBlogEditor />} />
        <Route path="blog/edit/:id" element={<AdminBlogEditor />} />
        <Route path="blog/analytics" element={<AdminBlogAnalytics />} />
        <Route path="product-revenue" element={<AdminProductRevenue />} />
        <Route path="feature-flags" element={<AdminSupportGuard><AdminFeatureFlagsTab /></AdminSupportGuard>} />
        <Route path="announcements" element={<AdminAnnouncementsTab />} />
        <Route path="tests" element={<Navigate to="/admin" replace />} />
        <Route path="team" element={<AdminSupportGuard><AdminTeamTab /></AdminSupportGuard>} />
        <Route path="settings" element={<AdminSupportGuard><AdminSettingsTab /></AdminSupportGuard>} />
        <Route path="audit" element={<AdminAuditLogTab />} />
        <Route path="blog" element={<AdminBlogTab />} />
        <Route path="newsletter" element={<AdminNewsletterTab />} />
        <Route path="embed-queue" element={<AdminSupportGuard><AdminEmbedQueueTab /></AdminSupportGuard>} />
        {/* Backward-compat redirects */}
        <Route path="reports" element={<Navigate to="/admin" replace />} />
        <Route path="early-customers" element={<Navigate to="/admin/organisations" replace />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <OrganizationProvider>
        <OnboardingProvider>
          <SessionTracker>
            <TooltipProvider>
              <Sonner />
              <BrowserRouter>
                <ScrollToTop />
                <MarketingTracker />
                <AppRoutes />
                
              </BrowserRouter>
            </TooltipProvider>
          </SessionTracker>
        </OnboardingProvider>
      </OrganizationProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
